import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import {
  FolderSimple, FolderSimplePlus, CaretRight, CaretDown, PencilSimple,
  Trash, Plus, Warning, FolderOpen, Check, X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Dossiers() {
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loadingCo, setLoadingCo] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const loadFolders = () => api.get("/folders").then((r) => setFolders(r.data));
  useEffect(() => { loadFolders(); }, []);

  const loadCompanies = (fid) => {
    setLoadingCo(true);
    api.get(`/folders/${fid}/companies`).then((r) => setCompanies(r.data)).finally(() => setLoadingCo(false));
  };
  const selectFolder = (f) => { setSelected(f); loadCompanies(f.id); };

  const createFolder = async (parent_id) => {
    const name = parent_id ? "Nouveau sous-dossier" : "Nouveau dossier";
    try {
      const { data } = await api.post("/folders", { name, parent_id: parent_id || null });
      await loadFolders();
      if (parent_id) setExpanded((e) => ({ ...e, [parent_id]: true }));
      setEditingId(data.id); setEditName(data.name);
    } catch { toast.error("Erreur création"); }
  };
  const rename = async (fid) => {
    if (!editName.trim()) { setEditingId(null); return; }
    try {
      await api.patch(`/folders/${fid}`, { name: editName.trim() });
      await loadFolders();
      if (selected?.id === fid) setSelected((s) => ({ ...s, name: editName.trim() }));
    } catch { toast.error("Erreur"); }
    setEditingId(null);
  };
  const removeFolder = async (f) => {
    if (!window.confirm(`Supprimer « ${f.name} » et ses sous-dossiers ? Les cabinets ne sont pas supprimés.`)) return;
    try {
      await api.delete(`/folders/${f.id}`);
      await loadFolders();
      if (selected?.id === f.id) { setSelected(null); setCompanies([]); }
      toast.success("Dossier supprimé");
    } catch { toast.error("Erreur suppression"); }
  };
  const removeCompany = async (cid) => {
    try {
      await api.delete(`/folders/${selected.id}/companies/${cid}`);
      setCompanies((c) => c.filter((x) => x.id !== cid));
      loadFolders();
    } catch { toast.error("Erreur"); }
  };

  // Aplatit l'arborescence en liste visible (selon l'état déplié)
  const visibleRows = [];
  const walk = (parentId, depth) => {
    folders.filter((f) => (f.parent_id || null) === parentId).forEach((f) => {
      const hasKids = folders.some((x) => x.parent_id === f.id);
      visibleRows.push({ f, depth, hasKids });
      if (expanded[f.id]) walk(f.id, depth + 1);
    });
  };
  walk(null, 0);

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl">Dossiers</h1>
        <button data-testid="new-root-folder-btn" onClick={() => createFolder(null)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-primary text-white hover:bg-blue-800">
          <FolderSimplePlus size={16} /> Nouveau dossier
        </button>
      </header>

      <div className="flex" style={{ minHeight: "calc(100vh - 4rem)" }}>
        <div className="w-80 shrink-0 border-r border-border bg-white py-3" data-testid="folder-tree">
          <div className="px-4 overline text-muted-foreground mb-2">Arborescence</div>
          {visibleRows.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              Aucun dossier.<br />Créez votre premier dossier (ex : « Build-up PACA »).
            </div>
          )}
          {visibleRows.map(({ f, depth, hasKids }) => {
            const isOpen = expanded[f.id];
            const isSel = selected?.id === f.id;
            return (
              <div key={f.id} data-testid={`folder-node-${f.id}`}
                className={`group flex items-center gap-1 pr-2 py-1.5 cursor-pointer border-l-2 ${isSel ? "border-primary bg-accent" : "border-transparent hover:bg-secondary"}`}
                style={{ paddingLeft: 8 + depth * 14 }}>
                <button onClick={() => setExpanded((x) => ({ ...x, [f.id]: !isOpen }))}
                  className={`text-muted-foreground ${hasKids ? "" : "invisible"}`}>
                  {isOpen ? <CaretDown size={13} /> : <CaretRight size={13} />}
                </button>
                {editingId === f.id ? (
                  <div className="flex items-center gap-1 flex-1">
                    <input autoFocus value={editName} onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") rename(f.id); if (e.key === "Escape") setEditingId(null); }}
                      data-testid={`rename-input-${f.id}`}
                      className="flex-1 px-2 py-1 text-sm border border-primary outline-none" />
                    <button onClick={() => rename(f.id)} className="text-emerald-600"><Check size={15} /></button>
                    <button onClick={() => setEditingId(null)} className="text-muted-foreground"><X size={15} /></button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5 flex-1 min-w-0" onClick={() => selectFolder(f)}>
                      {isOpen
                        ? <FolderOpen size={16} weight="fill" style={{ color: f.color || "#002FA7" }} />
                        : <FolderSimple size={16} weight="fill" style={{ color: f.color || "#002FA7" }} />}
                      <span className="text-sm truncate">{f.name}</span>
                      <span className="text-[11px] text-muted-foreground font-mono">{f.count}</span>
                    </div>
                    <div className="hidden group-hover:flex items-center gap-1">
                      <button title="Sous-dossier" data-testid={`add-subfolder-${f.id}`} onClick={() => createFolder(f.id)} className="text-muted-foreground hover:text-primary"><Plus size={14} /></button>
                      <button title="Renommer" data-testid={`rename-folder-${f.id}`} onClick={() => { setEditingId(f.id); setEditName(f.name); }} className="text-muted-foreground hover:text-primary"><PencilSimple size={14} /></button>
                      <button title="Supprimer" data-testid={`delete-folder-${f.id}`} onClick={() => removeFolder(f)} className="text-muted-foreground hover:text-destructive"><Trash size={14} /></button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex-1 p-8">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
              <FolderOpen size={48} className="mb-3 opacity-40" />
              <p className="text-sm">Sélectionnez un dossier pour voir les cabinets,<br />ou créez une arborescence dossiers / sous-dossiers.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-4">
                <FolderOpen size={20} weight="fill" style={{ color: selected.color || "#002FA7" }} />
                <h2 className="font-head font-bold text-lg">{selected.name}</h2>
                <span className="text-sm text-muted-foreground">· {companies.length} cabinet(s)</span>
              </div>
              <div className="bg-white border border-border overflow-x-auto" data-testid="folder-companies">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-muted-foreground">
                      <th className="text-left font-medium px-4 py-3 overline">Cabinet</th>
                      <th className="text-left font-medium px-4 py-3 overline">Localisation</th>
                      <th className="text-right font-medium px-4 py-3 overline">Commissions</th>
                      <th className="text-right font-medium px-4 py-3 overline">CA</th>
                      <th className="text-center font-medium px-4 py-3 overline">Cession</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingCo && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Chargement…</td></tr>}
                    {!loadingCo && companies.length === 0 && (
                      <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                        Dossier vide. Ajoutez des cabinets depuis « Deal Sourcing » (sélection multiple) ou la fiche cabinet.
                      </td></tr>
                    )}
                    {companies.map((c) => (
                      <tr key={c.id} data-testid={`folder-company-${c.id}`} className="border-b border-border hover:bg-secondary">
                        <td className="px-4 py-3 cursor-pointer" onClick={() => navigate(`/cabinet/${c.id}`)}>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold">{c.nom}</span>
                            {c.radie && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full"><Warning size={11} weight="fill" /> Radié</span>}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">SIREN {c.siren}</div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{c.ville}<div className="text-xs">{c.region}</div></td>
                        <td className="px-4 py-3 text-right font-mono">{eur(c.commissions)}</td>
                        <td className="px-4 py-3 text-right font-mono">{eur(c.ca)}</td>
                        <td className="px-4 py-3 text-center font-mono font-semibold">{c.scores.transmission}%</td>
                        <td className="px-4 py-3 text-right">
                          <button data-testid={`remove-from-folder-${c.id}`} onClick={() => removeCompany(c.id)}
                            title="Retirer du dossier" className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
